<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import BaseSettingsHeader from '../../components/BaseSettingsHeader.vue';
import SettingsLayout from '../../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import AlertResponza from 'dashboard/components-next/responza/AlertResponza.vue';
import { useAlert } from 'dashboard/composables';

const router = useRouter();
const store = useStore();
const { t } = useI18n();

// State
const accounts = ref([]);
const isLoading = ref(false);
const error = ref(null);
const showNoAccountsModal = ref(false);

// Get authentication headers from cookie
const getAuthHeaders = () => {
  const getCookie = name => {
    const match = document.cookie.match(
      new RegExp('(^| )' + name + '=([^;]+)')
    );
    return match ? decodeURIComponent(match[2]) : null;
  };

  const cookie = getCookie('cw_d_session_info');
  if (!cookie) return {};
  try {
    const sessionInfo = JSON.parse(cookie);
    return {
      'access-token': sessionInfo['access-token'],
      client: sessionInfo.client,
      uid: sessionInfo.uid,
      'Content-Type': 'application/json',
    };
  } catch (e) {
    console.error('Error parsing session cookie:', e);
    return {};
  }
};

// Filter to show only Official WhatsApp API accounts
const officialAccounts = computed(() => {
  return accounts.value.filter(account => account.integration_type === 'official');
});

// Get connection status badge class
const getConnectionStatusClass = (status) => {
  switch (status) {
    case 2: // Connected
      return 'bg-grass-50 text-grass-800 border-grass-200';
    case 1: // Connecting
      return 'bg-n-amber-1 text-n-amber-11 border-n-amber-4';
    case 0: // Disconnected
    default:
      return 'bg-n-ruby-1 text-n-ruby-11 border-n-ruby-4';
  }
};

// Get connection status text
const getConnectionStatusText = (status) => {
  switch (status) {
    case 2:
      return 'Connected';
    case 1:
      return 'Connecting';
    case 0:
    default:
      return 'Disconnected';
  }
};

// Fetch business profile for an account
const fetchBusinessProfile = async (inboxId) => {
  try {
    const response = await fetch(`/api/v1/whatsapp/${inboxId}/business_profile`, {
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      const data = await response.json();
      return data.profile;
    }
  } catch (err) {
    console.error(`Failed to load business profile for inbox ${inboxId}:`, err);
  }
  return null;
};

// Load WhatsApp accounts from the API
const loadAccounts = async () => {
  isLoading.value = true;
  error.value = null;

  try {
    const response = await fetch('/api/v1/bulk_messages/load_inboxes', {
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      const data = await response.json();

      // Process the results to format them properly for display
      // Filter to show only official WhatsApp API accounts
      const officialAccounts = (data.results || [])
        .filter(account => account.integration_type === 'official')
        .map(account => ({
          id: account.inbox_id,
          name: account.inbox_name || `WhatsApp Business API ${account.display_phone_number || account.inbox_id}`,
          integration_type: account.integration_type,
          integration_id: account.integration_id,
          inbox_id: account.inbox_id,
          display_phone_number: account.display_phone_number,
          channel_type: account.channel_type || 'whatsapp',
          provider: account.provider || 'whatsapp_cloud_api',
          instances: account.instances || [],
          // Official API is always connected if it exists
          connection_status: 2, // Always connected
          error: account.error,
          last_verified: account.last_verified,
          api_verified: account.api_verified,
          profile_picture_url: null // Will be loaded separately
        }));

      accounts.value = officialAccounts;

      // Fetch business profile for each account to get profile picture and phone
      for (const account of accounts.value) {
        const profile = await fetchBusinessProfile(account.inbox_id);
        if (profile) {
          // Set profile picture
          if (profile.profile_picture_url) {
            account.profile_picture_url = profile.profile_picture_url;
          }

          // Set display phone number with fallback
          // Priority: display_phone_number from profile, then phone_number from profile, then existing display_phone_number
          account.display_phone_number = profile.display_phone_number || profile.phone_number || account.display_phone_number;
        }
      }

      // Check if no accounts exist after loading
      if (accounts.value.length === 0) {
        showNoAccountsModal.value = true;
      }
    } else {
      const errorData = await response.json();
      error.value = errorData.error || 'Failed to load accounts';
      useAlert(error.value, 'error');
    }
  } catch (err) {
    console.error('Failed to load WhatsApp accounts:', err);
    error.value = 'Failed to load WhatsApp accounts';
    useAlert(error.value, 'error');
  } finally {
    isLoading.value = false;
  }
};

// Get connection status from account data
const getConnectionStatus = (account) => {
  if (account.integration_type === 'official') {
    // For official WhatsApp API, check if there are instances and their status
    if (account.instances && account.instances.length > 0) {
      const instance = account.instances[0];
      return instance.connectionStatus || 2; // Default to connected for official API
    }
    return 2; // Official API is usually always connected
  } else if (account.integration_type === 'evolution') {
    // For Evolution API, check instance connection status
    if (account.instances && account.instances.length > 0) {
      const connectedInstance = account.instances.find(instance => instance.connectionStatus === 2);
      if (connectedInstance) return 2;

      const connectingInstance = account.instances.find(instance => instance.connectionStatus === 1);
      if (connectingInstance) return 1;

      return 0; // Disconnected
    }
    return 0;
  }

  return 0; // Default to disconnected for unknown types
};

// Navigate to account profile settings
const openAccountProfile = (account) => {
  router.push({
    name: 'settings_whatsapp_account_profile',
    params: { inboxId: account.inbox_id }
  });
};

// Handle configure button click (same as clicking the card)
const configureAccount = (account, event) => {
  event?.stopPropagation(); // Prevent card click when clicking configure button
  openAccountProfile(account);
};

// Navigate to templates filtered by account
const navigateToAccountTemplates = (accountId) => {
  router.push({
    name: 'settings_whatsapp_templates',
    query: { accountId }
  });
};

// Format date for display
const formatDate = (dateString) => {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// Close no accounts modal
const closeNoAccountsModal = () => {
  showNoAccountsModal.value = false;
};

// Initialize on mount
onMounted(() => {
  loadAccounts();
});
</script>

<template>
  <SettingsLayout>
    <template #body>
      <!-- Header section -->
      <div class="flex flex-col items-start w-full pt-4">
        <div class="flex items-center justify-between w-full gap-4">
          <h1 class="text-2xl font-semibold font-interDisplay tracking-[0.3px] text-slate-900 dark:text-slate-25">
            <woot-sidemenu-icon />
            {{ $t('WHATSAPP_SETTINGS.ACCOUNTS.TITLE') }}
          </h1>
        </div>

        <div class="flex flex-col w-full gap-3 text-slate-600 dark:text-slate-300">
          <p class="mb-0 mt-2.5 text-base font-normal line-clamp-5 sm:line-clamp-none max-w-3xl tracking-[-0.1px]">
            {{ $t('WHATSAPP_SETTINGS.ACCOUNTS.DESCRIPTION') }}
          </p>
        </div>
      </div>

      <!-- Content section -->
      <div class="flex-grow flex-shrink overflow-auto mt-6">
        <!-- Loading state -->
        <div v-if="isLoading" class="flex justify-center items-center py-8">
          <Spinner size="medium" />
        </div>

        <!-- Error state -->
        <div v-else-if="error" class="rounded-2xl border border-n-ruby-4 bg-n-ruby-1 px-6 py-8 text-sm leading-6 text-n-ruby-11">
          <div class="flex items-center gap-2">
            <i class="i-lucide-alert-circle" />
            Error loading accounts: {{ error }}
          </div>
        </div>

        <!-- Accounts display -->
        <div v-else-if="accounts.length > 0" class="space-y-4">
        <!-- Official WhatsApp API Accounts - Full Width Cards -->
        <div
          v-for="account in officialAccounts"
          :key="`official-${account.id}`"
          class="border rounded-lg overflow-hidden border-n-slate-3 hover:border-n-slate-4 hover:shadow-sm transition-all cursor-pointer"
          @click="openAccountProfile(account)"
        >
          <div class="p-6">
            <div class="flex items-center justify-between">
              <!-- Account Info -->
              <div class="flex items-center gap-4">
                <!-- Profile Picture or Icon -->
                <div class="flex items-center justify-center w-12 h-12 bg-grass-50 rounded-lg overflow-hidden">
                  <img
                    v-if="account.profile_picture_url"
                    :src="account.profile_picture_url"
                    :alt="account.name"
                    class="w-full h-full object-cover"
                  />
                  <i v-else class="i-lucide-shield-check text-grass-700 text-xl" />
                </div>

                <div class="flex-1">
                  <div class="flex items-center gap-2 mb-1">
                    <h4 class="text-lg font-medium text-n-slate-12">{{ account.name }}</h4>
                    <span
                      class="px-2 py-1 text-xs rounded-full border bg-grass-50 text-grass-800 border-grass-200"
                    >
                      Connected
                    </span>
                  </div>

                  <div class="flex items-center gap-4 text-sm text-n-slate-11">
                    <div>ID: {{ account.inbox_id }}</div>
                    <div v-if="account.last_verified" class="flex items-center gap-1">
                      <i class="i-lucide-clock text-xs" />
                      Verified {{ formatDate(account.last_verified) }}
                    </div>
                  </div>

                  <div class="flex items-center gap-1 text-xs text-n-slate-10 mt-1">
                    <span v-if="account.display_phone_number" class="flex items-center gap-1">
                      <i class="i-lucide-phone text-xs" />
                      {{ account.display_phone_number }}
                    </span>
                  </div>
                  <div class="flex items-center gap-1 text-xs text-n-slate-10 mt-1">
                    <span>WhatsApp Business API</span>
                  </div>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="flex items-center gap-2">
                <Button
                  label="Gerenciar Templates"
                  variant="outline"
                  color="blue"
                  size="sm"
                  @click="(event) => { event.stopPropagation(); navigateToAccountTemplates(account.inbox_id); }"
                >
                  <template #icon>
                    <i class="i-lucide-file-text" />
                  </template>
                </Button>

                <Button
                  label="Configurar"
                  variant="solid"
                  color="slate"
                  size="sm"
                  @click="(event) => { event.stopPropagation(); configureAccount(account, event); }"
                >
                  <template #icon>
                    <i class="i-lucide-settings" />
                  </template>
                </Button>
              </div>
            </div>
          </div>
        </div>
        </div>

        <!-- Empty state -->
        <div v-else class="rounded-2xl border border-n-weak bg-n-alpha-2 px-6 py-8 text-center">
          <div class="text-n-slate-11 mb-2">
            Nenhuma conta do WhatsApp API Oficial configurada
          </div>
          <p class="text-sm text-n-slate-10">
            Configure integrações do WhatsApp Business API para começar a enviar mensagens usando templates oficiais do WhatsApp.
          </p>
        </div>
      </div>

      <!-- Content blocker overlay when no accounts modal is shown -->
      <div
        v-if="showNoAccountsModal"
        class="fixed inset-0 bg-black/10 backdrop-blur-[2px] z-40"
        style="pointer-events: none;"
      ></div>
    </template>

    <!-- No accounts blocking modal -->
    <AlertResponza
      :show="showNoAccountsModal"
      type="warn"
      title="Nenhuma conta do WhatsApp conectada"
      message="Você precisa conectar uma conta oficial do WhatsApp Business API para acessar estas configurações. Configure uma integração do WhatsApp através do menu de Integrações."
      confirm-text="OK"
      :show-close-button="false"
      @close="closeNoAccountsModal"
    />
  </SettingsLayout>
</template>
